import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Card, Form, Nav, ProgressBar } from 'react-bootstrap';
import classNames from 'classnames';

import IconButton from 'components/common/IconButton';

import EmetSectionForm from './EmetSectionForm';
import EmetBreak from './EmetBreak';
import EmetSuccess from './EmetSuccess';
import NavItemPill from './NavItemPill';
import EmetWelcome from './EmetWelcome';

import { certificationActions } from 'store/slices/certification/certificationSilce';
import { createScoreSheetEmet, fetchQuestionsAnswersBySectionId } from 'api/certifications/emet/questions';

import { emetSteps } from 'data/certifications/emetSteps';
import { errorNotification, handleApiError, shuffleArray, processQuestions  } from 'utils';


const WizardLayoutEmet = ({variant, validation, progressBar, certification}) => {
	const dispatch = useDispatch();
    const token = useSelector(state => state.authentication.user?.token);

    const { step, timeOver } = useSelector(state => state.certificationReducer);

	const [questions, setQuestions] = useState(null);
	const [currentQuestion, setCurrentQuestion] = useState({})
	const [selectedAnswer, setSelectedAnswer] = useState({});

	const [loading, setLoading] = useState(false);
	const [blocking, setBlocking] = useState(false);

	const isBreakStep = step => [3, 5, 7].includes(step);

    const getInitialTime = step => (isBreakStep(step) ? 600 : 60); 

	useEffect(() => {
		if ([1, 9].includes(step)) {
			dispatch(certificationActions.setInitialTime(0));
			dispatch(certificationActions.setStartTimer(false));
			dispatch(certificationActions.setShowTimer(false));
		} else if ([2, 4, 6, 8].includes(step)) {
			loadQuestionsSectionById(step / 2);
			dispatch(certificationActions.setInitialTime(getInitialTime(step)));
			dispatch(certificationActions.setStartTimer(true));
			dispatch(certificationActions.setShowTimer(true));
		} else if (isBreakStep(step)) {
			dispatch(certificationActions.setInitialTime(getInitialTime(step)));
			dispatch(certificationActions.setStartTimer(true));
			dispatch(certificationActions.setShowTimer(true));
		}

	}, [step]);


	useEffect(() => {
		if (questions === null) return;
		
        if (questions.length > 0)
            setCurrentQuestion({ ...questions[0] });
		else if (questions.length === 0) {
            dispatch(certificationActions.resetSection());
			dispatch(certificationActions.incrementStep());
			setCurrentQuestion({});
        }
    }, [questions]);


	useEffect(() => {
        if (timeOver ) {
			if (questions.length > 0) {
				if (Object.keys(selectedAnswer || {}).length === 0) {
					handleSubmitNoAnswer();
				} else {
					handleSubmitAnswer()
				}
			}
		}
    }, [timeOver]);


	const loadQuestionsSectionById = async (section_id) => {
        try {
			setLoading(true);  
            const response = await fetchQuestionsAnswersBySectionId(section_id, token);
            const { data } = response;
			if (data.status === 200) {
                const groupSize = step === 8 ? 2 : 3;
                const limit = 40; 
                
                const finalQuestions = processQuestions(data.message, groupSize, limit);
                const shuffledQuestions = shuffleArray(finalQuestions);

				setQuestions(shuffledQuestions);
            }
        } catch (error) {
            handleApiError(error);
			setLoading(false);
		} finally {
			setLoading(false);
		}
    };

	
	const handleSelectedAnswer = (answer) => {
        setSelectedAnswer(answer);
    };

	
	const handleSubmitAnswer = () => {
		setBlocking(true);
		if(selectedAnswer) {
			if (Object.keys(selectedAnswer).length === 0) {
				errorNotification("Es necesario elegir una opción");
			} else {
				let data = {
					certification_id: certification.id,
					section_id: currentQuestion.section_id,
					question_id: currentQuestion.question_id,
					answer_id: selectedAnswer.answer_id
				}
				handleCreateScoreSheetEmet(data);
			}
		} else {
			setBlocking(false);
			errorNotification("Es necesario elegir una opción");
		}
		
    };

	const handleSubmitNoAnswer = () => {
		setBlocking(true);
		let data = {
			certification_id: certification.id,
			section_id: currentQuestion.section_id,
			question_id: currentQuestion.question_id,
			answer_id: null
		}
		handleCreateScoreSheetEmet(data);
			
    };

	const handleCreateScoreSheetEmet = async (data) => {
		try {
			const response = await createScoreSheetEmet(data, token);
			if(response.data.status === 201) {
				setSelectedAnswer(null);
				
				setQuestions((prev) => prev.slice(1));
				 // Resetear el timer
				 dispatch(certificationActions.setStartTimer(false)); // Detener el timer
				 dispatch(certificationActions.setTimeOver(false)); // Resetear el estado de timeOver
				 setTimeout(() => {
					 dispatch(certificationActions.setStartTimer(true)); // Reiniciar el timer
				 }, 50);
				
			}	
		} catch (error) {
			handleApiError(error);
		} finally {
			setBlocking(false); 
		}
	}

	const renderStepComponent = () => {
		if (step === 1) return <EmetWelcome certification={certification} />;
		if (step === 9) return <EmetSuccess certification={certification} />;
		if ([2, 4, 6, 8].includes(step)) {
			return (
				<EmetSectionForm 
					key={currentQuestion?.id} // Usar la ID de la pregunta como clave
					currentQuestion={currentQuestion} 
					onSelectAnswer={handleSelectedAnswer} 
				/>
			);
		}
		if ([3, 5, 7].includes(step)) return <EmetBreak />;
		return null; // En caso de que no haya un componente válido
	};
	
	return (
		<>
			<Card as={Form} noValidate  className={`theme-wizard mb-5 `}>
				<Card.Header className={classNames('bg-light', {'px-4 py-3': variant === 'pills'})}>
					<Nav className="justify-content-center" variant={variant}>
						{emetSteps.map((item, index) => (
							<NavItemPill key={`${item.label}-${index}`}
								index={index + 1}
								step={step}
								icon={item.icon}
								label={item.label}
								sectionDesc={item.sectionDesc} /> 
						))}
					</Nav>
				</Card.Header>
				{progressBar && 
					<ProgressBar now={step * 11.11} style={{height: 2}} />
				}
				<Card.Body className="fw-normal px-md-6 py-4">
					{renderStepComponent()}
				</Card.Body>
				<Card.Footer className={classNames('px-md-6 bg-light', {'d-none': [1, 3, 5, 7, 9].includes(step), 'd-flex': [2, 4, 6, 8].includes(step)})}>
					{ [2, 4, 6, 8].includes(step) && (
						<IconButton
							variant="primary"
							className="ms-auto px-5"
							type="button"
							onClick={handleSubmitAnswer} // Llamar a la función de envío
							icon={'chevron-right'}
							iconAlign="right"
							transform="down-1 shrink-4"
							disabled={blocking} >
							Submit
						</IconButton>
					)} 
				</Card.Footer>
			</Card>
		</>
	);
};

export default WizardLayoutEmet;
